import React, { useLayoutEffect, useMemo, useRef } from "react";
import { Plus, Trash2, ChevronDown, ChevronUp, AlertTriangle } from "lucide-react";
import QuestionEditor, { newQuestion, InlineError, MIN_QUESTIONS_PER_COMPETENCY } from "./QuestionEditor";

const summarise = (q) => (q.scenario_text || "").trim() || "Untitled question";

const addQuestionCls =
  "border-btn dark:border-btn-dark dark:!text-white !text-textPrimary flex items-center gap-1.5 transition-all";

/**
 * The scenario text, edited in the accordion header so it isn't rendered twice.
 * Grows to fit its content, same as MCQ's QuestionTextField.
 */
const ScenarioTextField = ({ value, onChange, disabled }) => {
  const ref = useRef(null);

  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return;
    el.style.height = "auto";
    el.style.height = `${el.scrollHeight + el.offsetHeight - el.clientHeight}px`;
  }, [value]);

  return (
    <textarea
      ref={ref}
      rows={1}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      placeholder="Describe the scenario the candidate is reacting to..."
      maxLength={500}
      disabled={disabled}
      className={`w-full overflow-hidden resize-none rounded-lg border border-gray-300 dark:border-white/10 focus:border-purple-400 bg-bgPrimary dark:glass-effect dark:!text-white !text-textPrimary px-2.5 py-1.5 text-sm leading-5 outline-none transition-colors ${disabled ? "opacity-60 cursor-not-allowed" : ""}`}
    />
  );
};

const CompetencySelect = ({ value, competencies, onChange, disabled }) => (
  <select
    value={value || ""}
    onChange={(e) => onChange(e.target.value)}
    disabled={disabled}
    className={`rounded-lg border border-gray-300 dark:border-white/10 bg-bgPrimary dark:glass-effect dark:text-white text-textPrimary text-xs px-2 py-1.5 outline-none focus:border-purple-400 ${disabled ? "opacity-60 cursor-not-allowed" : ""}`}
  >
    <option value="" disabled>Competency…</option>
    {competencies.map((c) => (
      <option key={c.id} value={c.id}>{c.name}</option>
    ))}
  </select>
);

const QuestionListPanel = ({
  questions = [],
  competencies = [],
  errors = {},
  onChange,
  expandedId,
  onExpandedChange,
  locked = false,
}) => {
  const updateQuestion = (updated) =>
    onChange(questions.map((q) => (q.id === updated.id ? updated : q)));

  // No default competency — silently defaulting to competencies[0] meant every
  // new question landed inside whichever group that happened to be, wherever
  // that group sat in the list, instead of where the recruiter was looking.
  // Left unassigned, it renders in its own group (below) until picked.
  const addQuestion = () => {
    const q = newQuestion("");
    onChange([...questions, q]);
    onExpandedChange(q.id);
  };

  const removeQuestion = (id) => onChange(questions.filter((q) => q.id !== id));

  const competencyName = (id) => competencies.find((c) => c.id === id)?.name || "Unassigned";

  // Stable numbers matching the Navigator grid (which numbers by this same
  // original order) — grouping below only changes display order, never these.
  const numberById = useMemo(
    () => Object.fromEntries(questions.map((q, i) => [q.id, i + 1])),
    [questions]
  );

  // Order of first appearance, same as perCompetency in CreateBehavioralSet.jsx,
  // so this list's group order matches the Summary panel's row order. Unassigned
  // always sorts last — new questions land there, next to "Add question" below.
  const groups = useMemo(() => {
    const list = [];
    const indexByCompetency = new Map();
    questions.forEach((q) => {
      const cid = q.competency_id || "";
      if (!indexByCompetency.has(cid)) {
        indexByCompetency.set(cid, list.length);
        list.push({ competency_id: cid, questions: [] });
      }
      list[indexByCompetency.get(cid)].questions.push(q);
    });
    const unassignedIndex = list.findIndex((g) => g.competency_id === "");
    if (unassignedIndex !== -1 && unassignedIndex !== list.length - 1) {
      list.push(list.splice(unassignedIndex, 1)[0]);
    }
    return list;
  }, [questions]);

  return (
    <div className="flex-1 w-full min-w-0 space-y-3">
      {questions.length > 0 && (
        <div>
          <h3 className="text-sm font-semibold dark:text-white text-textPrimary">
            Questions{" "}
            <span className="dark:text-gray-500 text-textSecondary font-normal">
              ({questions.length})
            </span>
          </h3>
          <p className="text-xs dark:text-gray-500 text-textSecondary mt-0.5">
            Scenarios and rankings are yours to edit — starting content may include
            platform-provided defaults.
          </p>
        </div>
      )}

      {questions.length === 0 && (
        <div className="min-h-[420px] flex items-center justify-center rounded-xl border border-dashed border-white/15 lg:w-[calc(100%+19.5rem)]">
          <div className="px-4 text-center space-y-6">
            <div>
              <p className="text-sm dark:text-gray-400 text-textSecondary">No questions yet.</p>
              <p className="text-xs dark:text-gray-500 text-textSecondary mt-1">
                Add one to get started.
              </p>
            </div>
            <div className="flex items-center justify-center">
              <button onClick={addQuestion} disabled={locked} className={`${addQuestionCls} ${locked ? "opacity-60 cursor-not-allowed" : ""}`}>
                <Plus size={15} /> Add question
              </button>
            </div>
          </div>
        </div>
      )}

      {groups.map((group) => {
        const isUnassigned = group.competency_id === "";
        const groupCount = group.questions.length;
        const groupShort = !isUnassigned && groupCount < MIN_QUESTIONS_PER_COMPETENCY;
        return (
          <div key={group.competency_id || "unassigned"} className="space-y-3">
            <div className="flex items-center gap-2 pt-1">
              <span className={`text-xs font-semibold uppercase tracking-wide ${isUnassigned ? "text-amber-400" : "dark:text-gray-400 text-textSecondary"}`}>
                {isUnassigned ? "New — pick a competency" : competencyName(group.competency_id)}
              </span>
              <span className={`text-[11px] ${groupShort ? "text-amber-400 font-medium" : "dark:text-gray-500 text-textSecondary"}`}>
                {groupCount} question{groupCount === 1 ? "" : "s"}{groupShort ? ` (needs ${MIN_QUESTIONS_PER_COMPETENCY})` : ""}
              </span>
              <div className="flex-1 border-t border-gray-300 dark:border-white/10" />
            </div>

            {group.questions.map((q) => {
              const i = numberById[q.id] - 1;
              const isOpen = expandedId === q.id;
              const error = errors[q.id];
              return (
                <div
                  key={q.id}
                  id={`behavioral-q-${q.id}`}
                  className={`group rounded-xl border transition-colors scroll-mt-24 ${
                    error
                      ? "border-red-400/40"
                      : "border-gray-300 dark:border-white/10 bg-bgSecondary dark:glass-effect"
                  } ${isOpen ? "" : "hover:bg-black/[0.04] dark:hover:bg-white/[0.04]"}`}
                >
                  <div
                    className={`flex gap-2 px-4 ${
                      isOpen ? "pt-2.5 items-start" : "py-2.5 items-center"
                    }`}
                  >
                    {isOpen ? (
                      <span className="text-sm font-semibold dark:text-gray-500 text-textSecondary shrink-0 w-5 text-right mt-1.5">
                        {i + 1}
                      </span>
                    ) : (
                      <button
                        onClick={() => onExpandedChange(q.id)}
                        title="Expand question"
                        aria-expanded={false}
                        className="flex items-center gap-2 text-left flex-1 min-w-0"
                      >
                        <span className="text-sm font-semibold dark:text-gray-500 text-textSecondary shrink-0 w-5 text-right">
                          {i + 1}
                        </span>
                        <span className="text-sm dark:text-white text-textPrimary truncate">
                          {summarise(q)}
                        </span>
                      </button>
                    )}

                    {isOpen && (
                      <div className="flex-1 min-w-0 space-y-1.5">
                        <CompetencySelect
                          value={q.competency_id}
                          competencies={competencies}
                          onChange={(competency_id) => updateQuestion({ ...q, competency_id })}
                          disabled={locked}
                        />
                        <ScenarioTextField
                          value={q.scenario_text}
                          onChange={(scenario_text) => updateQuestion({ ...q, scenario_text })}
                          disabled={locked}
                        />
                        <InlineError msg={error} />
                      </div>
                    )}

                    {error && (
                      <AlertTriangle
                        size={14}
                        className={`text-red-400 shrink-0 ${isOpen ? "mt-2" : ""}`}
                        title={error}
                      />
                    )}
                    <button
                      onClick={() => removeQuestion(q.id)}
                      title="Delete question"
                      disabled={locked}
                      className={`p-1.5 rounded-md text-gray-400 dark:text-gray-600 hover:text-red-400 transition-all duration-150 shrink-0 disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:text-gray-400 dark:disabled:hover:text-gray-600 ${
                        isOpen ? "mt-0.5" : "opacity-0 group-hover:opacity-100 focus:opacity-100"
                      }`}
                    >
                      <Trash2 size={14} />
                    </button>

                    <button
                      onClick={() => onExpandedChange(isOpen ? null : q.id)}
                      title={isOpen ? "Collapse question" : "Expand question"}
                      aria-expanded={isOpen}
                      aria-label={`${isOpen ? "Collapse" : "Expand"} question ${i + 1}`}
                      className={`p-1.5 ml-1 rounded-md dark:text-gray-400 text-textSecondary hover:text-textPrimary dark:hover:text-white transition-colors shrink-0 ${
                        isOpen ? "mt-0.5" : ""
                      }`}
                    >
                      {isOpen ? <ChevronUp size={15} /> : <ChevronDown size={15} />}
                    </button>
                  </div>

                  {isOpen && (
                    <div className="px-4 pb-3 pt-1.5 border-t border-white/10">
                      <QuestionEditor question={q} onChange={updateQuestion} locked={locked} />
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        );
      })}

      {questions.length > 0 && (
        <div className="flex justify-center pt-1">
          <button onClick={addQuestion} disabled={locked} className={`${addQuestionCls} ${locked ? "opacity-60 cursor-not-allowed" : ""}`}>
            <Plus size={15} /> Add question
          </button>
        </div>
      )}
    </div>
  );
};

export default QuestionListPanel;
